import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';

/**
 * Get phone dialer configuration and availability status
 * GET /api/calls/config
 *
 * Call this FIRST when opening the dialer to check if calling is available.
 *
 * @returns {Promise<{
 *   enabled: boolean,
 *   configured: boolean,
 *   callerId: string|null,
 *   agentPhone: string|null,
 *   tier: string,
 *   requiredTier: string,
 *   creditBalance: number,
 *   message: string|null
 * }>}
 */
export async function getDialerConfig() {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/config`);

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Please log in to use phone calling');
        }
        if (response.status === 403) {
            const data = await response.json();
            throw new Error(data.detail?.message || 'Phone calling not available for your subscription');
        }
        logError('Failed to get dialer config', response.status);
        throw new Error('Failed to load phone dialer');
    }

    return response.json();
}

/**
 * Initiate an outbound phone call
 * POST /api/calls/initiate
 *
 * @param {Object} params
 * @param {string} params.phoneNumber - Phone number to call (E.164 format)
 * @param {string} [params.agentPhone] - Agent's phone for desk phone mode (E.164)
 * @param {string} [params.leadId] - Optional lead ID to link call
 * @param {string} [params.caseId] - Optional case ID to link call
 * @param {string} params.contactName - Display name for the contact
 * @returns {Promise<{success: boolean, callId: string, status: string, startTime: string}>}
 */
export async function initiateCall({ phoneNumber, agentPhone, leadId, caseId, contactName }) {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            phoneNumber,
            agentPhone,
            leadId,
            caseId,
            calleeName: contactName
        })
    });

    if (!response.ok) {
        logError('Failed to initiate call', response.status);
        const error = new Error('Failed to initiate call');
        error.status = response.status;
        throw error;
    }

    return response.json();
}

/**
 * End an active phone call
 * POST /api/calls/:callId/end
 *
 * @param {string} callId - The call ID to end
 * @param {string} [reason] - Optional reason for ending call
 * @returns {Promise<{success: boolean, callId: string, endTime: string, duration: number}>}
 */
export async function endCall(callId, reason = 'COMPLETED') {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/${callId}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
    });

    if (!response.ok) {
        logError(`Failed to end call ${callId}`, response.status);
        throw new Error('Failed to end call');
    }

    return response.json();
}

/**
 * Get details of a specific call
 * GET /api/calls/:callId
 *
 * @param {string} callId - The call ID
 * @returns {Promise<Object>} Call details
 */
export async function getCallDetails(callId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/${callId}`);

    if (!response.ok) {
        logError(`Failed to get call details for ${callId}`, response.status);
        throw new Error('Failed to get call details');
    }

    return response.json();
}

/**
 * Get real-time transcript for a call
 * GET /api/calls/:callId/transcript
 *
 * @param {string} callId - The call ID
 * @returns {Promise<{callId: string, status: string, segments: Array, lastUpdated: string}>}
 */
export async function getTranscript(callId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/${callId}/transcript`);

    if (!response.ok) {
        // Don't throw on 404 - transcript may not be ready yet
        if (response.status === 404) {
            return { callId, status: 'PENDING', segments: [] };
        }
        logError(`Failed to get transcript for ${callId}`, response.status);
        throw new Error('Failed to get transcript');
    }

    return response.json();
}

/**
 * Generate AI summary of a call
 * POST /api/calls/:callId/summarize
 *
 * @param {string} callId - The call ID
 * @param {Object} [options] - Summary options
 * @param {boolean} [options.includeActionItems=true] - Include action items
 * @param {boolean} [options.includeSentiment=true] - Include sentiment analysis
 * @returns {Promise<{callId: string, summary: Object, generatedAt: string}>}
 */
export async function generateCallSummary(callId, options = {}) {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/${callId}/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            includeActionItems: options.includeActionItems ?? true,
            includeSentiment: options.includeSentiment ?? true,
            customPrompt: options.customPrompt || null
        })
    });

    if (!response.ok) {
        logError(`Failed to generate summary for call ${callId}`, response.status);
        throw new Error('Failed to generate call summary');
    }

    return response.json();
}

/**
 * Save call summary as a case comment
 * POST /api/calls/:callId/comment
 *
 * @param {string} callId - The call ID
 * @param {string} caseId - The case ID to add comment to
 * @param {string} summary - The summary text to save
 * @param {Object} [options] - Additional options
 * @returns {Promise<{success: boolean, commentId: string, caseId: string}>}
 */
export async function saveCallAsComment(callId, caseId, summary, options = {}) {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/${callId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            caseId,
            summary,
            includeActionItems: options.includeActionItems ?? true,
            includeTranscriptLink: options.includeTranscriptLink ?? false
        })
    });

    if (!response.ok) {
        logError(`Failed to save call ${callId} as comment`, response.status);
        throw new Error('Failed to save call as comment');
    }

    return response.json();
}

/**
 * Get call history for a lead or case
 * GET /api/calls/history
 *
 * @param {Object} params
 * @param {string} [params.leadId] - Filter by lead ID
 * @param {string} [params.caseId] - Filter by case ID
 * @param {number} [params.limit=20] - Max number of calls to return
 * @returns {Promise<{calls: Array, totalCount: number, hasMore: boolean}>}
 */
export async function getCallHistory({ leadId, caseId, limit = 20 } = {}) {
    const queryParams = new URLSearchParams();
    if (leadId) queryParams.append('leadId', leadId);
    if (caseId) queryParams.append('caseId', caseId);
    queryParams.append('limit', String(limit));

    const response = await fetchWithAuth(`${BASE_URL}/api/calls/history?${queryParams.toString()}`);

    if (!response.ok) {
        logError('Failed to fetch call history', response.status);
        throw new Error('Failed to fetch call history');
    }

    return response.json();
}

/**
 * Update call metadata (e.g., link to case after call)
 * PATCH /api/calls/:callId
 *
 * @param {string} callId - The call ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated call details
 */
export async function updateCall(callId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/calls/${callId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        logError(`Failed to update call ${callId}`, response.status);
        throw new Error('Failed to update call');
    }

    return response.json();
}

// ============================================
// Phone Number Management APIs (Ultra+ only)
// ============================================

/**
 * Get all phone numbers owned by the user
 * GET /api/phone/numbers
 *
 * @returns {Promise<{numbers: Array, total: number}>}
 */
export async function getPhoneNumbers() {
    const response = await fetchWithAuth(`${BASE_URL}/api/phone/numbers`);

    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('Phone number management requires Ultra subscription');
        }
        logError('Failed to fetch phone numbers', response.status);
        throw new Error('Failed to fetch phone numbers');
    }

    return response.json();
}

/**
 * Get details of a specific phone number
 * GET /api/phone/numbers/{phone_id}
 *
 * @param {string} phoneId - The phone number ID
 * @returns {Promise<Object>} Phone number details
 */
export async function getPhoneNumberDetails(phoneId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/phone/numbers/${phoneId}`);

    if (!response.ok) {
        logError(`Failed to get phone number ${phoneId}`, response.status);
        throw new Error('Failed to get phone number details');
    }

    return response.json();
}

/**
 * Update a phone number's settings
 * PATCH /api/phone/numbers/{phone_id}
 *
 * @param {string} phoneId - The phone number ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.display_name] - Display name for the number
 * @param {string} [updates.team_id] - Team ID to assign
 * @param {string} [updates.team_name] - Team name
 * @param {string} [updates.status] - "active" or "suspended"
 * @returns {Promise<Object>} Updated phone number
 */
export async function updatePhoneNumber(phoneId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/phone/numbers/${phoneId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        logError(`Failed to update phone number ${phoneId}`, response.status);
        throw new Error('Failed to update phone number');
    }

    return response.json();
}

/**
 * Assign a phone number to a team
 * POST /api/phone/numbers/{phone_id}/assign
 *
 * @param {string} phoneId - The phone number ID
 * @param {string} teamId - The team ID to assign
 * @param {string} teamName - The team name
 * @returns {Promise<Object>} Updated phone number
 */
export async function assignPhoneToTeam(phoneId, teamId, teamName) {
    const response = await fetchWithAuth(`${BASE_URL}/api/phone/numbers/${phoneId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_id: teamId, team_name: teamName })
    });

    if (!response.ok) {
        logError(`Failed to assign phone ${phoneId} to team`, response.status);
        throw new Error('Failed to assign phone number to team');
    }

    return response.json();
}

/**
 * Unassign a phone number from a team
 * DELETE /api/phone/numbers/{phone_id}/assign
 *
 * @param {string} phoneId - The phone number ID
 * @returns {Promise<Object>} Updated phone number
 */
export async function unassignPhoneFromTeam(phoneId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/phone/numbers/${phoneId}/assign`, {
        method: 'DELETE'
    });

    if (!response.ok) {
        logError(`Failed to unassign phone ${phoneId} from team`, response.status);
        throw new Error('Failed to unassign phone number from team');
    }

    return response.json();
}

/**
 * Get usage stats for a phone number
 * GET /api/phone/numbers/{phone_id}/usage
 *
 * @param {string} phoneId - The phone number ID
 * @returns {Promise<Object>} Usage statistics
 */
export async function getPhoneUsage(phoneId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/phone/numbers/${phoneId}/usage`);

    if (!response.ok) {
        logError(`Failed to get usage for phone ${phoneId}`, response.status);
        throw new Error('Failed to get phone usage');
    }

    return response.json();
}

/**
 * Get usage history for a phone number
 * GET /api/phone/numbers/{phone_id}/usage/history
 *
 * @param {string} phoneId - The phone number ID
 * @param {number} [months=6] - Number of months of history
 * @returns {Promise<{phone_id: string, history: Array}>}
 */
export async function getPhoneUsageHistory(phoneId, months = 6) {
    const response = await fetchWithAuth(
        `${BASE_URL}/api/phone/numbers/${phoneId}/usage/history?months=${months}`
    );

    if (!response.ok) {
        logError(`Failed to get usage history for phone ${phoneId}`, response.status);
        throw new Error('Failed to get phone usage history');
    }

    return response.json();
}

/**
 * Get call logs for a phone number
 * GET /api/phone/numbers/{phone_id}/calls
 *
 * @param {string} phoneId - The phone number ID
 * @param {Object} [options]
 * @param {number} [options.limit=50] - Max calls to return
 * @param {number} [options.offset=0] - Offset for pagination
 * @returns {Promise<{calls: Array, total: number}>}
 */
export async function getPhoneCallLogs(phoneId, { limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const response = await fetchWithAuth(
        `${BASE_URL}/api/phone/numbers/${phoneId}/calls?${params}`
    );

    if (!response.ok) {
        logError(`Failed to get call logs for phone ${phoneId}`, response.status);
        throw new Error('Failed to get call logs');
    }

    return response.json();
}

/**
 * Get the phone number assigned to a team
 * GET /api/phone/team/{team_id}
 *
 * @param {string} teamId - The team ID
 * @returns {Promise<Object|null>} Team's phone number or null
 */
export async function getTeamPhoneNumber(teamId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/phone/team/${teamId}`);

    if (!response.ok) {
        if (response.status === 404) {
            return null; // Team has no phone number assigned
        }
        logError(`Failed to get phone for team ${teamId}`, response.status);
        throw new Error('Failed to get team phone number');
    }

    return response.json();
}

/**
 * Get call logs for a team's phone number
 * GET /api/phone/team/{team_id}/calls
 *
 * @param {string} teamId - The team ID
 * @param {Object} [options]
 * @param {number} [options.limit=50] - Max calls to return
 * @returns {Promise<{calls: Array, total: number}>}
 */
export async function getTeamCallLogs(teamId, { limit = 50 } = {}) {
    const response = await fetchWithAuth(
        `${BASE_URL}/api/phone/team/${teamId}/calls?limit=${limit}`
    );

    if (!response.ok) {
        logError(`Failed to get call logs for team ${teamId}`, response.status);
        throw new Error('Failed to get team call logs');
    }

    return response.json();
}
